'use client'

import { Container } from '@/shared/components/Container'
import { animate, motion } from 'framer-motion'
import React, { useRef, useState } from 'react'
import { UserReviewSection } from '@/lib/apis/sanity'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/sharp-solid-svg-icons'
import { useIsMobile } from '@/hooks/use-mobile'
import clsx from 'clsx'

type Props = {
  content: UserReviewSection
}

export default function ReviewSectionV2({ content }: Props) {
  const currentSnappedReview = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reviewDivs = useRef([] as Array<HTMLElement>)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMobile = useIsMobile(640)

  function findTranslateX(el: HTMLElement): number {
    const style = window.getComputedStyle(el)
    const matrix = new WebKitCSSMatrix(style.transform)
    return matrix.m41
  }

  return (
    <div className="bg-clinical-white">
      <Container className="space-y-12 py-20">
        <div className="flex flex-col items-center gap-3">
          <h2 className="text-center font-moisette text-4xl font-bold text-rich-black">
            {content.title}
          </h2>
          <p className="text-center font-psans text-base text-steel-navy">
            {content.subtitle}
          </p>
        </div>
        <div className="w-full overflow-hidden" ref={wrapperRef}>
          <motion.div
            ref={containerRef}
            className="flex cursor-grab active:cursor-grabbing"
            drag="x"
            dragMomentum={false}
            onDragEnd={() => {
              if (!wrapperRef.current) return

              const viewPortLeft = wrapperRef.current.getBoundingClientRect().x
              let nearestIndex = 0
              let nearest: HTMLElement | null = null

              for (let i = 0; i < reviewDivs.current.length; i++) {
                const current = reviewDivs.current[i]
                const rect = current.getBoundingClientRect()
                const center = rect.left + rect.width / 2

                // The first item that has their left border on the screen is the
                // one we would like to snap to.
                if (
                  center > viewPortLeft ||
                  (isMobile && i === reviewDivs.current.length - 1) ||
                  (!isMobile && i === reviewDivs.current.length - 2)
                ) {
                  nearest = current
                  nearestIndex = i
                  break
                }
              }

              if (nearest === null) throw new Error('No nearest element found')

              const rect = nearest.getBoundingClientRect()
              const offset = viewPortLeft - rect.left
              const translateX = findTranslateX(containerRef.current!)

              animate(
                containerRef.current!,
                {
                  // Shift the current translate by offset amount
                  x: translateX + offset,
                },
                {
                  ease: ['easeOut'],
                },
              )

              currentSnappedReview.current = nearest
              setCurrentIndex(nearestIndex)
            }}
          >
            {content.reviews.map((review, index) => (
              <div key={index} className="w-full shrink-0 px-2.5 sm:w-1/2">
                <div
                  className="bg-off-white flex h-full flex-col items-center rounded-3xl p-2.5"
                  ref={(el) => {
                    // Normally we don't need to check the refs length on normal rendering as
                    // this component will most likely be rendered only once. But when developing,
                    // the rendering will be run multiple times and need to handle that here.
                    if (index < reviewDivs.current.length)
                      reviewDivs.current[index] = el!
                    else reviewDivs.current = [...reviewDivs.current, el!]

                    if (index === 0 && currentSnappedReview.current === null)
                      currentSnappedReview.current = el!
                  }}
                >
                  <div className="flex grow flex-col items-center justify-start gap-y-4 rounded-xl bg-white p-7">
                    <div className="flex items-center">
                      {[0, 0, 0, 0, 0].map((_, idx) => (
                        <FontAwesomeIcon
                          key={idx}
                          className="text-yellow-500"
                          icon={faStar}
                        />
                      ))}
                    </div>
                    <p className="text-pretty text-center font-psans text-lg leading-snug text-rich-black">
                      {'"'}
                      {review.content}
                      {'"'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center justify-center py-4">
                    <p className="font-psans text-lg font-semibold text-rich-black">
                      {review.author}
                    </p>
                    <p className="font-psans text-base text-rich-black">
                      {review.city}, {review.state}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        <div className="flex items-center justify-center gap-x-1.5">
          {content.reviews
            .slice(
              0,
              isMobile ? content.reviews.length : content.reviews.length - 1,
            )
            .map((_, index) => (
              <div
                key={index}
                className={clsx(
                  'h-2 w-8 rounded-full bg-[#E8E8DC]',
                  currentIndex === index && '!bg-blue-600',
                  !isMobile &&
                    currentIndex === index &&
                    currentIndex === content.reviews.length - 2 &&
                    '!bg-blue-600',
                )}
              />
            ))}
        </div>
      </Container>
    </div>
  )
}
